import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

interface Article {
  id: number;
  title: string;
  content: string;
  image: string | null;
  created_at: string;
  category: string;
  cultivation_stage: string;
  views: number;
  status: string;
  summary: string;
  author_name?: string;
  tags: string[];
}

const ArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    
    fetch(`https://fellfell.ir/api/articles.php?action=single&id=${id}`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          setArticle(data.data);
        } else {
          setError(data.message || 'مقاله یافت نشد');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('خطا در بارگذاری مقاله');
        setLoading(false);
      });
  }, [id]);

  const getStageBadge = (stage: string) => {
    const stages: Record<string, { label: string; color: string; bg: string; icon: string }> = {
      'کشت': { label: 'کشت (کاشت)', color: '#2e7d32', bg: '#e8f5e9', icon: '🌱' },
      'داشت': { label: 'داشت (رشد)', color: '#1565c0', bg: '#e3f2fd', icon: '🌿' },
      'برداشت': { label: 'برداشت', color: '#f57c00', bg: '#fff3e0', icon: '🌾' },
      'نگهداشت': { label: 'نگهداشت', color: '#6a1b9a', bg: '#f3e5f5', icon: '🏺' }
    };
    return stages[stage] || { label: stage, color: '#333', bg: '#f5f5f5', icon: '📌' };
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>⏳ در حال بارگذاری مقاله...</div>;
  }

  if (error || !article) {
    return (
      <div style={{ padding: '20px', color: '#c62828', background: '#ffebee', borderRadius: '10px' }}>
        ❌ {error || 'مقاله یافت نشد'}
      </div>
    );
  }

  const stage = getStageBadge(article.cultivation_stage || 'کشت');

  return (
    <>
      <div style={{ background: '#1e3a1e', color: '#fff', padding: '40px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Link to="/articles" style={{ color: '#a5d6a7', textDecoration: 'none', fontSize: '14px' }}>
            ← بازگشت به لیست مقالات
          </Link>
          <h1 style={{ fontSize: '28px', marginTop: '15px' }}>{article.title}</h1>
          <div style={{ display: 'flex', gap: '20px', marginTop: '15px', flexWrap: 'wrap', fontSize: '14px', color: '#a5d6a7' }}>
            <span>👤 {article.author_name || 'نویسنده'}</span>
            <span>📅 {new Date(article.created_at).toLocaleDateString('fa-IR')}</span>
            <span>👁️ {article.views || 0} بازدید</span>
            {article.category && <span>📂 {article.category}</span>}
            <span style={{ background: stage.bg, color: stage.color, padding: '2px 12px', borderRadius: '20px', fontWeight: '600' }}>
              {stage.icon} {stage.label}
            </span>
          </div>
        </div>
      </div>
      
      <section style={{ padding: '30px 0' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 20px' }}>
          {article.image && (
            <img 
              src={`https://fellfell.ir/uploads/articles/${article.image}`} 
              alt={article.title} 
              style={{ 
                width: '100%', 
                borderRadius: '12px', 
                marginBottom: '25px', 
                maxHeight: '450px', 
                objectFit: 'cover',
                background: '#f0f4f0'
              }}
              onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-article.jpg'; }}
              loading="lazy"
              decoding="async"
            />
          )}
          
          {article.summary && (
            <div style={{ 
              background: '#f5faf5', 
              padding: '20px 25px', 
              borderRadius: '12px', 
              marginBottom: '25px',
              borderRight: '5px solid #2e7d32',
              fontSize: '15px',
              lineHeight: '1.9',
              color: '#2e5a2e'
            }}>
              <strong style={{ color: '#1e3a1e' }}>📌 خلاصه:</strong> {article.summary}
            </div>
          )}
          
          {article.tags && article.tags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '25px' }}>
              {article.tags.map((tag, index) => (
                <span key={index} style={{ padding: '4px 14px', background: '#e3f2fd', color: '#0d47a1', borderRadius: '20px', fontSize: '13px' }}>
                  #{tag}
                </span>
              ))}
            </div>
          )}
          
          {/* ── نمایش محتوای HTML ────────────────────────── */}
          <div 
            className="article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
          
          <style>{`
            .article-content {
              color: #333;
              direction: rtl;
              text-align: justify;
              line-height: 2;
              font-size: 16px;
            }
            .article-content h1, .article-content h2, .article-content h3,
            .article-content h4, .article-content h5, .article-content h6 {
              color: #1e3a1e;
              margin-top: 1.5em;
              margin-bottom: 0.5em;
              line-height: 1.5;
            }
            .article-content h1 { font-size: 2em; }
            .article-content h2 { font-size: 1.7em; }
            .article-content h3 { font-size: 1.4em; }
            .article-content p { margin-bottom: 1em; line-height: 2; }
            .article-content ul, .article-content ol { padding-right: 1.5em; margin-bottom: 1em; }
            .article-content li { margin-bottom: 0.5em; }
            .article-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 1em 0; }
            .article-content blockquote {
              border-right: 4px solid #2e7d32;
              background: #f5faf5;
              padding: 15px 20px;
              margin: 1em 0;
              border-radius: 8px;
              color: #1e3a1e;
            }
            .article-content table { width: 100%; border-collapse: collapse; margin: 1em 0; }
            .article-content table th, .article-content table td { border: 1px solid #ddd; padding: 8px 12px; text-align: right; }
            .article-content table th { background: #e8f5e9; color: #1e3a1e; }
            .article-content a { color: #2e7d32; text-decoration: underline; }
            .article-content pre {
              background: #f5f5f5;
              padding: 15px;
              border-radius: 8px;
              overflow-x: auto;
              font-family: 'Courier New', monospace;
              font-size: 14px;
              direction: ltr;
              text-align: left;
            }
            .article-content code {
              background: #f5f5f5;
              padding: 2px 6px;
              border-radius: 4px;
              font-family: 'Courier New', monospace;
              font-size: 14px;
            }
          `}</style>
        </div>
      </section>
    </>
  );
};

export default ArticleDetail;